import {SelectionModel} from '@angular/cdk/collections';
import {ChangeDetectorRef, Inject, Injectable, OnDestroy, TemplateRef} from '@angular/core';
import {BehaviorSubject, combineLatest, isObservable, Observable, of, Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {DisabledObj} from '../../../../generated/generated-core.model';
import {MX_PERMISSION_TOKEN, MxPermissionService} from '../../../shared/services/mx-permission.service';
import {CopyPasteConfig} from '../../../shared/interfaces/copy-paste-config';
import {MxCopyPasteService} from '../../../shared/services/mx-copy-paste.service';
import {MxNotificationService} from '../../../shared/services/mx-notification.service';
import {MxButtonModel, MxButtonsConfig, MxButtonTooltipFn} from '../../mx-button/components/mx-button.model';
import {MxPreloaderService, PreloaderConfig, withPreloader} from '../../mx-preloader/services/mx-preloader.service';
import {MxGridCollectionService} from './mx-grid-collection.service';
import {MxGridCustomizerEvent} from './mx-grid-customizer-event';
import {MxDefaultGridButtonsPermissionsService} from './mx-grid-default-grid-buttons-permissions.service';
import {DefaultGridButtons} from './mx-grid-enums';
import {MxSelection} from './selection-type.model';
import {MxGridOrderService} from './mx-grid-order.service';
import {MxGridPropsService} from './mx-grid-props.service';
import {
    MxGridButtonsConfig,
    MxGridButtonModel,
    MxGridButtonHiddenFn,
    MxGridButtonDisableFn,
    MxGridButtonTooltipFn,
    MxGridButtonIconFn
} from './mx-grid-button.model';

export interface GridActionService {
    runAction(name: string, selection: SelectionModel<any>): void;
    refreshButtons(): void;
}

@Injectable()
export class MxGridActionService implements GridActionService, OnDestroy {

    private defaultGridButtonsConfig: MxGridButtonsConfig = new MxGridButtonsConfig([
        [DefaultGridButtons.filter, {
            order: 0,
            icon: 'filter_list',
            tooltip: 'Toggle filters',
            permission: 'all',
            className: 'mxs-button--round mxs-button--secondary',
            hidden: true
        }],
        [DefaultGridButtons.reload, {
            order: 1,
            icon: 'refresh',
            tooltip: 'Reload data',
            permission: 'all',
            className: 'mxs-button--round mxs-button--secondary',
            clickHandler: () => this.collectionService.doReload(),
        }],
        [DefaultGridButtons.add, {
            order: 2,
            icon: 'add',
            tooltip: 'Add item',
            className: 'mxs-button--round mxs-button--primary-cyan',
            clickHandler: (): void => {
                this.collectionService.goToItemDetails('new', 'new');
            }
        }],
        [DefaultGridButtons.edit, {
            order: 3,
            icon: 'edit',
            tooltip: 'Edit item',
            className: 'mxs-button--round mxs-button--secondary',
            disabled: MxSelection.noneOrMultipleSelection,
            clickHandler: (selection: SelectionModel<any>): void => {
                this.orderService.flush();
                this.collectionService.goToItemDetails(this.collectionService.extractId(selection.selected[0]), 'edit');
            }
        }],
        [DefaultGridButtons.view, {
            order: 4,
            icon: 'visibility',
            tooltip: 'View item',
            className: 'mxs-button--round mxs-button--secondary',
            disabled: MxSelection.noneOrMultipleSelection,
            clickHandler: (selection: SelectionModel<any>): void => {
                this.orderService.flush();
                this.collectionService.goToItemDetails(this.collectionService.extractId(selection.selected[0]), 'readonly');
            }
        }],
        [DefaultGridButtons.remove, {
            order: 5,
            icon: 'delete_outline',
            tooltip: 'Delete selected items',
            className: 'mxs-button--round mxs-button--secondary',
            disabled: MxSelection.noneSelection,
            clickHandler: (selection: SelectionModel<any>): void => {
                this.collectionService.doRemove(selection.selected);
            }
        }],
        [DefaultGridButtons.copy, {
            order: 6,
            icon: 'filter_none',
            tooltip: 'Copy',
            className: 'mxs-button--round mxs-button--secondary',
            disabled: MxSelection.noneOrMultipleSelection,
            hidden: (): boolean => {
                return !this.copyPasteConfig;
            },
            clickHandler: (selection: SelectionModel<any>): void => {
                this.copy(selection.selected[0]);
                this.refreshButtons();
            }
        }], [DefaultGridButtons.paste, {
            order: 7,
            icon: 'save_alt',
            tooltip: 'Paste',
            className: 'mxs-button--round mxs-button--secondary',
            disabled: () => this.disablePaste(),
            hidden: (): boolean => {
                return !this.copyPasteConfig;
            },
            clickHandler: (): void => {
                this.paste();
            }
        }],
        [DefaultGridButtons.moveUp, {
            order: 8,
            name: 'move up',
            tooltip: 'Move Up',
            icon: 'arrow_upward',
            className: 'mxs-button--round mxs-button--secondary',
            hidden: (): boolean => {
                return !this.propsService.props.allowReorder;
            },
            disabled: () => MxSelection.noneOrMultipleSelection(this.collectionService.selection, () => {
                    if (this.collectionService.selectedIndex === 0) {
                        return 'Already on top';
                    }
                    return false;
                }),
            clickHandler: (): void => {
                this.orderService.onOrderUp();
            }
        }],
        [DefaultGridButtons.moveDown, {
            order: 9,
            name: 'move down',
            tooltip: 'Move Down',
            icon: 'arrow_downward',
            className: 'mxs-button--round mxs-button--secondary',
            hidden: (): boolean => {
                return !this.propsService.props.allowReorder;
            },
            disabled: () => MxSelection.noneOrMultipleSelection(this.collectionService.selection, () => {
                    if (this.collectionService.selectedIndex === this.collectionService.rows.length - 1) {
                        return 'Already on bottom';
                    }
                    return false;
            }),
            clickHandler: (): void => {
                this.orderService.onOrderDown();
            }
        }]
    ]);

    private gridActions: MxButtonsConfig = new MxButtonsConfig();

    private _buttonsTemplate: TemplateRef<any>;
    private copyPasteConfig: CopyPasteConfig;
    private preloaderConfig: PreloaderConfig;
    private buttonRefresher$$: Subject<any> = new BehaviorSubject<any>(null);
    private _destroy$$: Subject<null> = new Subject<null>();

    get buttonsTemplate(): TemplateRef<any> {
        return this._buttonsTemplate;
    }

    constructor(private collectionService: MxGridCollectionService,
                private orderService: MxGridOrderService,
                private propsService: MxGridPropsService,
                private copyPasteService: MxCopyPasteService,
                private buttonsPermissionService: MxDefaultGridButtonsPermissionsService,
                private notificationService: MxNotificationService,
                @Inject(MX_PERMISSION_TOKEN) private permissionService: MxPermissionService,
                private cdRef: ChangeDetectorRef,
                private preloader: MxPreloaderService) {
    }

    public setUp(event: MxGridCustomizerEvent): void {
        this._buttonsTemplate = event.buttonsTemplate;
        this.copyPasteConfig = event.copyPasteConfig;
        this.preloaderConfig = event.preloaderConfig;


        // Combine actions such that last customizer will take precedence
        this.buttonsPermissionService.setGridButtonPermission(event.permission, this.defaultGridButtonsConfig);
        let combinedActions: MxGridButtonsConfig = event.actions.reduce(
            (acc, curr) => this.combineActions(acc, curr, event.permission), this.defaultGridButtonsConfig);

        let editButton = combinedActions.get(DefaultGridButtons.edit);
        let viewButton = combinedActions.get(DefaultGridButtons.view);

        if (!editButton.hidden && (!editButton.permission || this.permissionService.hasPermission(editButton.permission))) {
            viewButton.hidden = true;
            combinedActions.set(DefaultGridButtons.view, viewButton);
        } else if (!!editButton.permission && !this.permissionService.hasPermission(editButton.permission)
            && this.permissionService.hasPermission(viewButton.permission)
            && viewButton.hidden && viewButton.disabled !== true) {
            viewButton.hidden = false;
            combinedActions.set(DefaultGridButtons.view, viewButton);
        }

        let selectionChange$: Observable<SelectionModel<any>> = this.collectionService
            .rowsOrSelectionChanged()
            .pipe(map(s => s.selection));

        let buttonsConfig: MxButtonsConfig = new MxButtonsConfig();

        combinedActions.sort().forEach((gridButtonModel, name) => {
            gridButtonModel.name = name;
            buttonsConfig.set(name, this.createButtonInput(gridButtonModel, selectionChange$));
        });

        this.gridActions = buttonsConfig;
    }

    public refreshButtons(): void {
        this.buttonRefresher$$.next(null);
    }

    ngOnDestroy(): void {
        this._destroy$$.next(null);
    }

    private combineActions(acc: MxGridButtonsConfig, curr: MxGridButtonsConfig, permission: string): MxGridButtonsConfig {
        curr.forEach((customOverride, key) => {
            if (key === 'permission' && !customOverride.permission && !acc.get(key)) {
                customOverride.permission = permission;
            }
            acc.set(key, Object.assign(acc.get(key) || new MxGridButtonsConfig(), customOverride));
        });
        return acc;
    }

    private createButtonInput(gridButtonModel: MxGridButtonModel, selectionChange$: Observable<SelectionModel<any>>): MxButtonModel {
        let clickHandler = () => {
            gridButtonModel.clickHandler(
                this.collectionService.selection,
                this.collectionService
            );
        };

        let hidden: boolean = (typeof gridButtonModel.hidden === 'undefined') ? undefined :
            (typeof gridButtonModel.hidden === 'boolean') ? gridButtonModel.hidden : true;

        let disabled: boolean = (typeof gridButtonModel.disabled === 'undefined') ? undefined :
            (typeof gridButtonModel.disabled === 'boolean') ? gridButtonModel.disabled : true;
        let buttonModel: MxButtonModel = {
            tooltip: gridButtonModel.tooltip,
            permission: gridButtonModel.permission,
            name: gridButtonModel.name,
            label: gridButtonModel.label,
            icon: gridButtonModel.icon,
            color: gridButtonModel.color,
            className: gridButtonModel.className,
            clickHandler: clickHandler,
            hidden: hidden,
            disabled: disabled
        };

        this.handleDisabledFn(gridButtonModel, selectionChange$, buttonModel);

        this.handleTooltipFn(gridButtonModel, selectionChange$, buttonModel);

        this.handleIconFn(gridButtonModel, selectionChange$, buttonModel);

        this.handleHiddenFn(gridButtonModel, selectionChange$, buttonModel);
        return buttonModel;
    }

    private handleDisabledFn(gridButtonModel: MxGridButtonModel,
            selectionChange$: Observable<SelectionModel<any>>,
            buttonModel: MxButtonModel): void {

        let baseTooltip = gridButtonModel.tooltip;
        if (typeof gridButtonModel.disabled === 'function' && selectionChange$) {
            combineLatest(selectionChange$, this.buttonRefresher$$)
                .pipe(takeUntil(this._destroy$$))
                .subscribe(
                ([change, _refresh]) => {
                    buttonModel.disabled = true;
                    let disabledFnResult: string | boolean | Observable<DisabledObj> =
                        (gridButtonModel.disabled as MxGridButtonDisableFn)(change);
                    if (isObservable(disabledFnResult)) {
                        disabledFnResult
                            .pipe(
                                takeUntil(this._destroy$$),
                                withPreloader(this.preloader)
                            )
                            .subscribe(result => {
                                if (result.isDisabled) {
                                    let tooltip = result.hint ? result.hint : baseTooltip;
                                    this.assignDisabledValue(buttonModel, true, tooltip);
                                } else {
                                    this.assignDisabledValue(buttonModel, false, baseTooltip);
                                }
                                this.cdRef.detectChanges();
                            });
                    } else if (typeof disabledFnResult === 'string') {
                        let disabled = !!disabledFnResult;
                        let tooltip = disabledFnResult ? disabledFnResult : baseTooltip;
                        this.assignDisabledValue(buttonModel, disabled, tooltip);
                    } else {
                        this.assignDisabledValue(buttonModel, disabledFnResult, baseTooltip);
                    }
                }
            );
        }
    }

    private handleTooltipFn(gridButtonModel: MxGridButtonModel,
                            selectionChange$: Observable<SelectionModel<any>>,
                            buttonModel: MxButtonModel): void {
        if (typeof gridButtonModel.tooltip === 'function' && selectionChange$) {
            combineLatest(selectionChange$, this.buttonRefresher$$)
                .pipe(takeUntil(this._destroy$$))
                .subscribe(
                    ([change, _refresh]) => {
                        buttonModel.tooltip = (gridButtonModel.tooltip as MxGridButtonTooltipFn)(change);
                    }
                );
        }
    }

    private handleIconFn(gridButtonModel: MxGridButtonModel,
                         selectionChange$: Observable<SelectionModel<any>>,
                         buttonModel: MxButtonModel): void {
        if (typeof gridButtonModel.icon === 'function' && selectionChange$) {
            combineLatest(selectionChange$, this.buttonRefresher$$)
                .pipe(takeUntil(this._destroy$$))
                .subscribe(
                    ([change, _refresh]) => {
                        buttonModel.icon = (gridButtonModel.icon as MxGridButtonIconFn)(change);
                    }
                );
        }

    }

    private assignDisabledValue(buttonModel: MxButtonModel, disabled: boolean, tooltip: string | MxButtonTooltipFn): void {
        buttonModel.disabled = disabled;
        buttonModel.tooltip = tooltip;
    }

    private handleHiddenFn(gridButtonModel: MxGridButtonModel,
            selectionChange$: Observable<SelectionModel<any>>,
            buttonModel: MxButtonModel): void {

        if (typeof gridButtonModel.hidden === 'function' && selectionChange$) {
            buttonModel.hidden = true;
            combineLatest(selectionChange$, this.buttonRefresher$$)
                .pipe(takeUntil(this._destroy$$))
                .subscribe(
                ([change, _refresh]) => {
                    buttonModel.hidden = (gridButtonModel.hidden as MxGridButtonHiddenFn)(change);
                }
            );
        }
    }

    public runAction(name: string, _selection: SelectionModel<any>): void {
        const actionHandler: MxButtonModel = this.gridActions.get(name);
        if (actionHandler) {
            actionHandler.clickHandler();
        } else {
            throw new Error(`Action ${name} is not registered`);
        }
    }

    public getActionAllowedForDblClick(names: string[]): string {
        // we don't want to check disabled status of action, because it's depends on selection,
        // while this function is used for double click
         let allowed = names.map(name => {
             const actionHandler: MxButtonModel = this.gridActions.get(name);
             return {
                name: name,
                value: this.permissionService.hasPermission(actionHandler.permission) && !actionHandler.hidden
             };
         }
        ).filter(value => value.value === true);
        return allowed.length > 0 ? allowed[0].name : null;
    }

    public getActions(): Array<MxButtonModel> {
        return this.gridActions.getArray();
    }

    public isEditAllowed(): boolean {
        let boButtonModel = this.gridActions.get(DefaultGridButtons.edit);
        if (boButtonModel) {
            let editPermission = boButtonModel.permission;
            return this.permissionService.hasPermission(editPermission);
        }
        return false;
    }

    copy(value: any): void {
        this.copyPasteService.setData(this.copyPasteConfig.classType,
            this.copyPasteConfig.saveInGrid ? value : this.collectionService.extractId(value),
            this.copyPasteConfig.convertFn);
        this.notificationService.showSuccess('Row was copied');
    }

    paste(): void {
        if (this.copyPasteConfig.saveInGrid) {
            let newRows = [...this.collectionService.rows];
            let valueToPaste = {...<object>this.copyPasteService.getData(this.copyPasteConfig.classType, this.copyPasteConfig.convertFn)};
            newRows.push(JSON.parse(JSON.stringify(valueToPaste)));
            this.collectionService.setRows(newRows);
            this.notificationService.showSuccess('Row was pasted');
        } else {
            this.collectionService.goToItemDetails('new', 'duplicate', {}, true);
        }
    }

    private disablePaste(): string | false {
        if (!this.copyPasteConfig || !this.copyPasteConfig.classType || !(this.copyPasteService.canPaste(this.copyPasteConfig.classType)
            && ((!this.copyPasteService.isFullObject(this.copyPasteConfig.classType) && !this.copyPasteConfig.saveInGrid)
                || (this.copyPasteService.isFullObject(this.copyPasteConfig.classType) && this.copyPasteConfig.saveInGrid)))) {
                    return 'Nothing to paste';
                }
        return false;
    }

}
